import Store from '@/store/index.js'
// v9 moduler 方式
import { firebaseApp } from '@/firebase.js'
import { getAuth, onAuthStateChanged, signInAnonymously, signOut } from 'firebase/auth'
import { getFirestore, doc, getDoc, setDoc, writeBatch, serverTimestamp } from 'firebase/firestore'
import { getTeamIdsByUserId } from '@/api/FirebaseTeamWrapper.js'
import { getProfileImageUrl } from '@/api/Utilities.js'
import * as constant from '@/constant/constant.js'
const auth = getAuth(firebaseApp)
const db = getFirestore(firebaseApp)
const DOMAIN_NAME = constant.DEFAULT_DOMAIN_NAME

/**
 * getMyUserId:
 *   authentication 経由で確実にログインユーザーのuserIdを返す
 * @param {object} options
 * @returns {string|null}
 */
export async function getMyUserId () {
  if (location.hostname !== DOMAIN_NAME) { console.log(`getMyUserId()`) }
  if (auth.currentUser) {
    return auth.currentUser.uid
  }
  return new Promise((resolve, reject) => {
    onAuthStateChanged(auth, async (user) => {
      if (user) {
        return resolve(user.uid)
      } else {
        if (location.hostname !== DOMAIN_NAME) { console.log(`userId: null`) }
        /* 未ログインなので匿名ログインする */
        const userData = await signInAnonymously(auth)
        user = userData.user
        return resolve(user.uid)
      }
    })
  })
}

/**
 * getUserData:
 *   ユーザのpublicなデータを取得する
 *   Store に userData がキャッシュされていればそれを使う
 *   なければ DB から get() する
 *   その際 Store にも反映する
 *   削除済みや匿名ユーザーなど、不明なユーザーを取得しようとした場合はnullを返す
 *
 * @param {string} userId
 * @param {object} options
 * @returns
 */
export async function getUserData (userId, options = {noStore: false}) {
  if (location.hostname !== DOMAIN_NAME) { console.log(`getUserData(${userId}): options: ${JSON.stringify(options)}`) }
  const noStore = options.noStore
  let userDataMap = new Map(Store.state.userDataMap)
  let userData = noStore ? null : userDataMap.get(userId)

  if (userData) { // noStore -> null, Storeにない -> undefined
    return userData
  }

  const user = await getDoc(doc(db, 'socialV2', userId))
  if (!user.exists()) {
    console.log(`unknown user: ${userId}`)
    return null
  }
  userData = user.data()
  userDataMap.set(userId, userData)
  Store.commit('setUserDataMap', userDataMap)

  return userData
}

/**
 * getMyUserData:
 *   詳細なユーザデータを取得する
 *   自分自身以外のデータを取得しようとすると失敗する
 *
 *   1. userId にもとづいてデータを取得する
 *   2. userId がなければ改めて取得する(指定できるならしたほうが早い)
 *   3. 見つからなければ匿名ユーザのデータを返す
 * @param {string} userId
 * @returns {object} userData
 */
export async function getMyUserData (userId = null) {
  if (location.hostname !== DOMAIN_NAME) { console.log(`getMyUserData(${userId})`) }

  /* userId がなければ取得する */
  if (!userId) {
    userId = await getMyUserId()
  }

  try {
    const userDocRef = doc(db, 'uids', userId)
    const user = await getDoc(userDocRef)

    if (!user.exists()) {
      // サインインしているがDBにないユーザだということ
      if (location.hostname !== DOMAIN_NAME) { console.log(`getMyUserData: ${userId} does not exist.`) }
      return getAnonymousData()
    }
    if (location.hostname !== DOMAIN_NAME) { console.log(`getMyUserData: ${userId} exists.`) }
    // authから情報を取得する
    const currentUser = auth.currentUser
    const userData = {
      ...user.data(),
      ...{id: userId},
      ...{providerId: currentUser.providerData[0].providerId},
      ...{emailVerified: currentUser.emailVerified}
    }
    Store.commit('setUserData', userData)
    return userData
  } catch (err) {
    // おそらく匿名ユーザなのでパーミッションエラー
    console.log(`getMyUserData: ${userId} is maybe anonymous user. ${err}`)
    return getAnonymousData()
  }
}

/**
   * updateMyUserData:
   *   自分自身の情報を更新する
   *
   *   トランザクション処理:
   *     /uids/{userId}
   *     /socialV2/{userId}
   *     nameの変更時は /teams/{teamId}/members/{userId}.name
   *
   * @param {*} userData
   * @returns
   */
export async function updateMyUserData (userData) {
  if (location.hostname !== DOMAIN_NAME) { console.log(`updateMyUserData(${JSON.stringify(userData)})`) }

  /* トランザクション処理の開始 */
  const batch = writeBatch(db)

  const userId = await getMyUserId()

  let publicUserData = {updatedAt: serverTimestamp()}
  let privateUserData = {updatedAt: serverTimestamp()}
  if (typeof userData !== 'object') {
    return null
  }
  if ((typeof userData.email === 'string') && (userData.email !== '')) {
    privateUserData.email = userData.email
  }
  if (typeof userData.emailFlag === 'boolean') {
    privateUserData.emailFlag = userData.emailFlag
  }
  if ((typeof userData.navigation === 'string') && (userData.navigation !== '')) {
    privateUserData.navigation = userData.navigation
  }
  if ((typeof userData.nodeDirection === 'string') && (userData.nodeDirection !== '')) {
    privateUserData.nodeDirection = userData.nodeDirection
  }
  if ((typeof userData.imageUrl === 'string') && (userData.imageUrl !== '')) {
    privateUserData.imageUrl = userData.imageUrl
    publicUserData.imageUrl = userData.imageUrl
  }
  if ((typeof userData.introduction === 'string') && (userData.introduction !== '')) {
    privateUserData.introduction = userData.introduction
    publicUserData.introduction = userData.introduction
  }
  if ((typeof userData.name === 'string') && (userData.name !== '')) {
    privateUserData.name = userData.name
    publicUserData.name = userData.name
    const teamIds = await getTeamIdsByUserId(userId)
    for (const teamId of teamIds) {
      const teamMemberRef = doc(db, `teams/${teamId}/members`, userId)
      batch.update(teamMemberRef, {name: userData.name})
    }
  }
  if (typeof userData.soundFlag === 'boolean') {
    privateUserData.soundFlag = userData.soundFlag
  }
  if (typeof userData.clipBoardFlag === 'boolean') {
    privateUserData.clipBoardFlag = userData.clipBoardFlag
  }
  if (typeof userData.twitterName === 'string') {
    privateUserData.twitterName = userData.twitterName
    publicUserData.twitterName = userData.twitterName
  }
  if (typeof userData.url === 'string') {
    privateUserData.url = userData.url
    publicUserData.url = userData.url
  }

  const userRef = doc(db, 'uids', userId)
  batch.update(userRef, privateUserData)
  const socialRef = doc(db, 'socialV2', userId)
  batch.update(socialRef, publicUserData)

  await batch.commit()
}

/**
 * signUpQnQ:
 *   初回ログインの時 user.uid を userId としてDBに登録する
 *   引数 user.uid が自身の uid でない時はエラーになる
 * @params {string} uid
 * @returns {object} userData ユーザデータ
 */
export async function signUpQnQ (user) {
  if (location.hostname !== DOMAIN_NAME) { console.log('signUpQnQ()') }
  const userName = user.displayName
  const createdAt = serverTimestamp()
  const myImageUrl = getProfileImageUrl()
  // 公開用のuserDataの作成
  const publicUserData = {
    createdAt: createdAt,
    imageUrl: myImageUrl,
    introduction: 'よろしくお願いします',
    isAnonymous: false,
    name: userName,
    teams: [],
    twitterName: '',
    updatedAt: createdAt,
    url: ''
  }
  await setDoc(doc(db, 'socialV2', user.uid), publicUserData)
  if (location.hostname !== DOMAIN_NAME) { console.log('signUpQnQ(): success setDoc(socialV2)') }
  // 非公開用のuserDataの作成
  const email = user.email
  const privateUserData = {
    ...publicUserData,
    ...{
      clipBoardFlag: true,
      email: email,
      emailFlag: true,
      navigation: 'trackpad',
      nodeDirection: 'depth',
      soundFlag: true
    }
  }
  await setDoc(doc(db, 'uids', user.uid), privateUserData)
  if (location.hostname !== DOMAIN_NAME) { console.log('signUpQnQ(): success setDoc(uids)') }
  return privateUserData
}

/**
 * signOutQnQ:
 *   サインアウトして匿名ユーザになる
 *   Storeの値は初期化する
 */
export async function signOutQnQ () {
  await signOut(auth)
  if (location.hostname !== DOMAIN_NAME) { console.log('success signOut') }
  await signInAnonymously(auth)
  Store.commit('setViewingPage', '')
  await resetStoreUserData()
}

/**
 * resetStoreUserData:
 *   Store を userData でリセットする
 *   匿名ユーザの時は匿名ユーザの初期値が入る
 * @param {string} userId
 */
export async function resetStoreUserData () {
  if (location.hostname !== DOMAIN_NAME) { console.log(`resetStoreUserData(${JSON.stringify(Store.state.user)})`) }
  Store.commit('setUserData', null)

  const userId = await getMyUserId()
  const userData = await getMyUserData(userId)

  Store.commit('setUserData', userData)
  Store.commit('setUserDataMap', new Map())

  let socialDataMap = new Map()
  const socialData = await getUserData(userId)

  socialDataMap.set(userId, socialData)
  Store.commit('setUserDataMap', socialDataMap)
  if (location.hostname !== DOMAIN_NAME) { console.log(`resetStoreUserData after ${JSON.stringify(Store.state.user)}`) }
}

/**
 * getAnonymousData:
 *   匿名ユーザのユーザデータの初期値を得る
 * @returns object
 */
export function getAnonymousData () {
  return {
    clipBoardFlag: false,
    createdAt: new Date(),
    email: null,
    emailFlag: false,
    id: null,
    imageUrl: 'https://firebasestorage.googleapis.com/v0/b/secondchat-chat.appspot.com/o/users%2FnoName%2Fprofile?alt=media&token=34b3cd1f-0200-46f3-ac4e-eb8d2cfba19e',
    introduction: '',
    isAnonymous: true,
    name: 'Anonymous',
    navigation: 'trackpad',
    nodeDirection: 'depth',
    soundFlag: false,
    teams: [],
    twitterName: '',
    updatedAt: new Date(),
    url: ''
  }
}



// WEBPACK FOOTER //
// ./src/api/FirebaseUserWrapper.js